import { useEffect } from 'react';
import { Avatar } from '@klaro7/ui';

import { useCall } from '../context/CallContext';
import { CallerTypeBadge } from './CallerTypeBadge';
import { startRingtone, stopRingtone } from '../lib/ringtone';

export function IncomingCallModal() {
  const { incomingCall, callState, acceptIncomingCall, rejectIncomingCall } = useCall();

  // Ring via the shared (pre-primed) AudioContext so it is actually audible.
  useEffect(() => {
    if (callState !== 'incoming' || !incomingCall) return;

    startRingtone();

    // Tactile fallback: vibrate in a loop while ringing. Helps when audio is
    // blocked/silenced (e.g. iOS mute switch silences Web Audio). navigator.vibrate
    // is a no-op on iOS Safari but works on Android Chrome.
    const vibrate = () => {
      try { navigator.vibrate?.([500, 300, 500]); } catch { /* unsupported */ }
    };
    vibrate();
    const vibrateInterval = setInterval(vibrate, 1800);

    // Safety auto-stop after 30s (the ring window).
    const timeout = setTimeout(stopRingtone, 30000);

    return () => {
      stopRingtone();
      clearInterval(vibrateInterval);
      try { navigator.vibrate?.(0); } catch { /* unsupported */ }
      clearTimeout(timeout);
    };
  }, [callState, incomingCall]);

  if (callState !== 'incoming' || !incomingCall) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm animate-in fade-in slide-in-from-bottom-4 rounded-2xl bg-white p-8 text-center shadow-2xl">
        {/* Pulsating avatar */}
        <div className="mb-6 flex justify-center">
          <div className="relative">
            <div className="absolute inset-0 animate-ping rounded-full bg-green-400/30" style={{ animationDuration: '1.5s' }} />
            <Avatar
              fallback={incomingCall.callerName}
              size="lg"
            />
          </div>
        </div>

        <h2 className="text-xl font-bold text-neutral-900">{incomingCall.callerName}</h2>
        {incomingCall.callerType && (
          <div className="mt-2 flex justify-center">
            <CallerTypeBadge callerType={incomingCall.callerType} variant="light" />
          </div>
        )}
        {incomingCall.callType === 'chat' ? (
          <p className="mt-1 flex items-center justify-center gap-1.5 text-sm text-neutral-600">
            <svg className="h-4 w-4 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.86 9.86 0 01-4-.83L3 20l1.17-3.5A7.96 7.96 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            Richiesta di chat da {incomingCall.callerName}
          </p>
        ) : (
          <p className="mt-1 text-sm text-neutral-600">
            {incomingCall.callType === 'video' ? 'Videochiamata in arrivo' : 'Chiamata audio in arrivo'}
          </p>
        )}

        {incomingCall.callerStatus && (
          <div className="mt-3 flex justify-center">
            {incomingCall.callerStatus === 'converted' ? (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-semibold text-green-700">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Cliente convertito
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700">
                <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a1 1 0 011 1v1.07a7.002 7.002 0 015.93 5.93H18a1 1 0 110 2h-1.07a7.002 7.002 0 01-5.93 5.93V18a1 1 0 11-2 0v-1.07A7.002 7.002 0 013.07 11H2a1 1 0 110-2h1.07A7.002 7.002 0 019 4.07V3a1 1 0 011-1z" />
                </svg>
                Nuovo cliente
              </span>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-center gap-6">
          {/* Reject */}
          <button
            onClick={rejectIncomingCall}
            className="flex h-14 w-14 items-center justify-center rounded-full bg-red-500 text-white shadow-lg transition-transform hover:scale-105 hover:bg-red-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Accept */}
          <button
            onClick={acceptIncomingCall}
            className="flex h-16 w-16 items-center justify-center rounded-full bg-green-500 text-white shadow-lg transition-transform hover:scale-105 hover:bg-green-600"
          >
            <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}
